import React, { useCallback, useEffect, useState, useRef, useLayoutEffect, lazy, Component } from 'react';
import ReactDOM from 'react-dom';
import { CaseStudyHeader } from '../components/CaseStudyHeader';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { CaseStudyNavigation } from '../components/CaseStudyNavigation';
import { CaseStudyMobileHeader } from '../components/CaseStudyMobileHeader';
import { FloatingButtons } from '../components/FloatingButtons';
import { ChatDrawer } from '../components/ChatDrawer';
import { usePortfolio } from '../context/PortfolioContext';
import { useCaseStudyNavigation } from '../hooks/useCaseStudyNavigation';
import { SEO } from '../components/SEO';
import { StructuredData } from '../components/StructuredData';
import { ArrowLeft, CheckCircle, X, MagnifyingGlassPlus, CaretRight, Target, Clock, Users, Lightning, ChartBar, TrendUp } from '@phosphor-icons/react';
import { Link, useNavigate } from 'react-router-dom';
import '../styles/usbank.css';
import { useScrollAnimation } from '../utils/AnimationObserver';
import '../styles/animations.css';
import { motion, useScroll, useTransform } from 'framer-motion';
// Optimized Image Component
function SimpleImage({
  src,
  alt,
  className = ''
}: {
  src: string;
  alt: string;
  className?: string;
}) {
  const [isLoaded, setIsLoaded] = useState(false);
  return <div className={`overflow-hidden rounded-lg relative ${className}`} style={{
    contain: 'content'
  }} data-id="element-940">
      {!isLoaded && <div className="w-full h-full bg-gray-100 absolute inset-0 animate-pulse" data-id="element-941" />}
      <img src={src} alt={alt} loading="lazy" decoding="async" onLoad={() => setIsLoaded(true)} className={`w-full h-auto transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`} data-id="element-942" />
    </div>;
}
// Vertical Timeline Component (Replacing HorizontalTimeline)
function VerticalTimeline() {
  const timelineItems = [{
    number: '01',
    year: 'Phase 1',
    title: 'Content & Research',
    desc: 'Dedicated space for content strategy and user research findings.'
  }, {
    number: '02',
    year: 'Phase 2',
    title: 'Development Collaboration',
    desc: 'Active design work with real-time feedback loops.'
  }, {
    number: '03',
    year: 'Phase 3',
    title: 'Ready for Dev',
    desc: 'Finalized designs with accessibility annotations and handoff specs.'
  }];
  return <div className="relative py-12 ml-4 sm:ml-8" data-id="element-943">
      {/* Vertical Line */}
      <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-944" />

      <div className="space-y-24" data-id="element-945">
        {timelineItems.map((item, index) => <motion.div key={index} initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: index * 0.2
      }} className="relative flex items-start gap-8" data-id="element-946">
            {/* Timeline Dot with Number */}
            <div className="relative z-10 flex-shrink-0 w-12 h-12 rounded-full bg-[#001E79] ring-8 ring-white shadow-sm flex items-center justify-center mt-6" data-id="element-947">
              <span className="text-white font-bold text-sm" data-id="element-948">
                {item.number}
              </span>
            </div>

            {/* Content Card */}
            <div className="flex-1 bg-white p-8 rounded-2xl shadow-xl border border-gray-100 flex flex-col gap-4 group hover:-translate-y-2 transition-transform duration-300" data-id="element-949">
              <div className="w-12 h-1 bg-[#001E79] mb-2" data-id="element-950" />
              <span className="text-xs font-bold text-slate-400 tracking-widest uppercase" data-id="element-951">
                {item.year}
              </span>
              <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-952">
                {item.title}
              </h3>
              <p className="text-slate-600 leading-relaxed text-lg" data-id="element-953">
                {item.desc}
              </p>
            </div>
          </motion.div>)}
      </div>
    </div>;
}
// Modal Portal Component
function ModalPortal({
  children,
  isOpen
}: {
  children: React.ReactNode;
  isOpen: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);
  if (!mounted || !isOpen) return null;
  return ReactDOM.createPortal(children, document.body);
}
// Lightbox Image Component
function LightboxImage({
  src,
  alt,
  label
}: {
  src: string;
  alt: string;
  label?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <>
      <div className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-50 border border-gray-100" onClick={() => setIsOpen(true)} data-id="element-954">
        <img src={src} alt={alt} className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]" data-id="element-955" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center" data-id="element-956">
          <div className="opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300" data-id="element-957">
            <div className="bg-white text-black px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2" data-id="element-958">
              <MagnifyingGlassPlus className="w-4 h-4" data-id="element-959" />
              Zoom
            </div>
          </div>
        </div>
      </div>

      <ModalPortal isOpen={isOpen} data-id="element-960">
        <div onClick={() => setIsOpen(false)} className="fixed inset-0 bg-white/95 z-[99999] flex items-center justify-center p-4 sm:p-8 backdrop-blur-sm" data-id="element-961">
          <button onClick={e => {
          e.stopPropagation();
          setIsOpen(false);
        }} className="absolute top-6 right-6 p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-962">
            <X className="w-6 h-6 text-black" data-id="element-963" />
          </button>

          {label && <div className="absolute top-8 left-8 text-black text-xl font-medium" data-id="element-964">
              {label}
            </div>}

          <img src={src} alt={alt} onClick={e => e.stopPropagation()} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" data-id="element-965" />
        </div>
      </ModalPortal>
    </>;
}
interface CaseStudyUSBankProps {
  isOverlay?: boolean;
}
export function CaseStudyUSBank({
  isOverlay = false
}: CaseStudyUSBankProps) {
  const {
    viewPreference,
    linkedInProfile,
    isChatDrawerOpen,
    navigatedFromChat
  } = usePortfolio();
  const navigate = useNavigate();
  // UPDATED: Check if user came from chat OR home page
  const cameFromChat = sessionStorage.getItem('cameFromChat') === 'true';
  const cameFromHome = sessionStorage.getItem('navigatedFromHome') === 'true';
  const showBackButton = cameFromChat || cameFromHome;
  const {
    openChatDrawer,
    previousPage,
    setPreviousPage
  } = usePortfolio();
  // UPDATED: Handle back navigation to home
  const handleBackToHome = () => {
    // Clear navigation flags
    sessionStorage.removeItem('cameFromChat');
    sessionStorage.removeItem('navigatedFromHome');
    // Navigate first
    navigate('/');
    // FIXED: Scroll to top AFTER navigation completes
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };
  const homePath = viewPreference === 'ai' ? '/portfolio/ai' : '/portfolio/traditional';
  useCaseStudyNavigation();
  useScrollAnimation();
  // CRITICAL: Reset scroll position BEFORE any rendering
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);
  const [activeSection, setActiveSection] = useState('overview');
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    // Observe all sections
    const sections = ['overview', 'objectives', 'problem', 'solution', 'impact', 'reflection'];
    sections.forEach(sectionId => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });
    return () => {
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 128; // Account for sticky header height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleCaseStudyClick = (slug: string) => {
    console.log('Case study navigation handled by ChatDrawer:', slug);
  };
  const navItems = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'objectives',
    label: 'Objectives'
  }, {
    id: 'problem',
    label: 'Problem'
  }, {
    id: 'solution',
    label: 'Solution'
  }, {
    id: 'impact',
    label: 'Impact'
  }, {
    id: 'reflection',
    label: 'Reflection'
  }];
  return <>
      <SEO title="US Bank Commercial Card - UX Case Study" description="Redesigning the corporate card application process." keywords={['UX Design', 'Fintech', 'Case Study']} url="https://mateocohen.com/case-study/us-bank" type="article" image="https://uploadthingy.s3.us-west-1.amazonaws.com/cGTn1zWyBRLJq9z8XJ288f/usbthumb.png" data-id="element-966" />

      {!isOverlay && <CustomCursor data-id="element-967" />}

      <div className="flex min-h-screen bg-white text-black font-switzer" data-id="element-968">
        <div className="flex-1 min-w-0 flex flex-col relative" data-id="element-969">
          {!isOverlay && <CaseStudyHeader data-id="element-970" />}
          {!isOverlay && <CaseStudyMobileHeader data-id="element-971" />}

          {/* Hero Section - Clean White Background */}
          <section className="pt-32 pb-12 px-4 sm:px-8 md:px-12 lg:px-24 max-w-[1400px] mx-auto w-full relative" style={{
          zIndex: 100
        }} data-id="element-972">
            <div className="max-w-4xl" data-id="element-973">
              {/* Back to Portfolio button - always visible */}
              {!isOverlay && <button onClick={handleBackToHome} className="flex items-center gap-2 text-gray-600 hover:text-[#001E79] transition-colors mb-6 group" aria-label="Back to portfolio" style={{
              marginBottom: '28px'
            }} data-id="element-974">
                  <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-975" />
                  <span className="text-sm font-medium" data-id="element-976">Back to Portfolio</span>
                </button>}

              <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-8 text-[#001E79] leading-[1.1]" data-id="element-977">
                U.S. Bank Commercial Card
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl font-light" data-id="element-978">
                Transforming a 54-day manual corporate card application process
                into a streamlined digital platform.
              </p>
            </div>
          </section>

          {/* Full Width Hero Image */}
          <div className="w-full h-[60vh] sm:h-[80vh] overflow-hidden mb-24 relative" style={{
          zIndex: 100
        }} data-id="element-979">
            <img src="https://cdn.magicpatterns.com/uploads/qmk3kLu5tjWSuKq1Da1MRB/usbcasestudyhomepage.png" alt="US Bank Hero" className="w-full h-full object-cover object-top" data-id="element-980" />
          </div>

          {/* Main Content Grid */}
          <div className="max-w-[1400px] mx-auto w-full px-4 sm:px-8 md:px-12 lg:px-24 pb-32 relative" style={{
          zIndex: 100
        }} data-id="element-981">
            <div className="lg:grid lg:grid-cols-12 gap-12" data-id="element-982">
              {/* Sticky Navigation Sidebar */}
              <div className="hidden lg:block lg:col-span-3" data-id="element-983">
                <div className="sticky top-32 space-y-6" data-id="element-984">
                  {navItems.map(item => <button key={item.id} onClick={() => scrollToSection(item.id)} className={`block text-sm tracking-wide transition-colors duration-300 ${activeSection === item.id ? 'text-[#001E79] font-medium border-l-2 border-[#001E79] pl-4' : 'text-gray-400 hover:text-gray-600 pl-4 border-l-2 border-transparent'}`} data-id="element-985">
                      {item.label}
                    </button>)}
                </div>
              </div>

              {/* Content Column */}
              <div className="lg:col-span-9 space-y-32" data-id="element-986">
                {/* Summary Section */}
                <section id="overview" className="scroll-mt-32" data-id="element-987">
                  <div className="grid md:grid-cols-2 gap-16 mb-24" data-id="element-988">
                    <div data-id="element-989">
                      <h3 className="text-sm font-medium text-[#001E79] uppercase tracking-wider mb-2" data-id="element-990">
                        Role
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-991">Lead UX Designer</p>
                    </div>
                    <div data-id="element-992">
                      <h3 className="text-sm font-medium text-[#001E79] uppercase tracking-wider mb-2" data-id="element-993">
                        Project Duration
                      </h3>
                      <p className="text-lg text-slate-700" data-id="element-994">2 Years</p>
                    </div>
                  </div>

                  <h2 className="text-3xl font-light mb-8 text-[#001E79]" data-id="element-995">
                    Overview
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-996">
                    In 2022, I joined U.S. Bank's Team Flash to refine a newly
                    launched digital onboarding experience for corporate card
                    applications. The challenge was to transform a{' '}
                    <span className="font-semibold text-slate-800" data-id="element-997">
                      54-day manual process into a streamlined digital platform
                    </span>{' '}
                    while addressing critical usability issues that emerged
                    after launch.
                  </p>

                  <div className="bg-gray-50 rounded-2xl p-8 md:p-12 mb-12" data-id="element-998">
                    <h3 className="text-xl font-medium mb-8 text-[#001E79]" data-id="element-999">
                      Key Achievements
                    </h3>
                    <div className="space-y-4" data-id="element-1000">
                      {['Achieved <span class="font-semibold text-slate-800">98% customer rating of "easy" or "very easy"</span> for digital onboarding, exceeding 90% target', 'Reduced bank handling time by <span class="font-semibold text-slate-800">65%: from 54 days to just 20 days</span>', 'Successfully migrated <span class="font-semibold text-slate-800">96% of CPS pipeline to digital onboarding</span>, far surpassing 80% target', 'Unlocked <span class="font-semibold text-slate-800">20,000 hours of annual productivity</span> through streamlined digital solutions', 'Implemented <span class="font-semibold text-slate-800">17+ automated onboarding processes</span> using ServiceNow'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-1001">
                          <CheckCircle className="w-5 h-5 text-[#001E79] mt-1 flex-shrink-0" data-id="element-1002" />
                          <span className="text-slate-700 text-lg" dangerouslySetInnerHTML={{
                        __html: item
                      }} data-id="element-1003" />
                        </div>)}
                    </div>
                  </div>
                </section>

                {/* Objectives Section */}
                <section id="objectives" className="scroll-mt-32" data-id="element-1004">
                  <h2 className="text-3xl font-light mb-8 text-[#001E79]" data-id="element-1005">
                    Objectives
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1006">
                    This project aimed to transform corporate card onboarding
                    while achieving specific business outcomes and user
                    experience goals.
                  </p>

                  <div className="space-y-4 mb-24" data-id="element-1007">
                    {[{
                    title: 'Best-in-Class Experience',
                    desc: 'Deliver a seamless digital onboarding experience that rivals consumer fintech standards.'
                  }, {
                    title: 'Revenue Opportunities',
                    desc: 'Accelerate time-to-revenue by drastically reducing application processing times.'
                  }, {
                    title: 'Expand Impact',
                    desc: 'Create scalable workflows that can be deployed across multiple bank product lines.'
                  }, {
                    title: '>90% Satisfaction',
                    desc: 'Achieve customer satisfaction ratings exceeding 90% for ease of use.'
                  }, {
                    title: '>50% Time Reduction',
                    desc: 'Cut manual processing time by more than half through automation and self-service.'
                  }].map((card, idx) => <div key={idx} className="border border-gray-200 p-6 rounded-lg hover:border-gray-300 transition-colors" data-id="element-1008">
                        <div className="flex items-start gap-4" data-id="element-1009">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full border-2 border-[#001E79] flex items-center justify-center text-xs font-bold text-[#001E79]" data-id="element-1010">
                            {idx + 1}
                          </div>
                          <div className="flex-1" data-id="element-1011">
                            <h3 className="text-lg font-medium text-[#001E79] mb-2" data-id="element-1012">
                              {card.title}
                            </h3>
                            <p className="text-slate-600 leading-relaxed" data-id="element-1013">
                              {card.desc}
                            </p>
                          </div>
                        </div>
                      </div>)}
                  </div>
                </section>

                {/* Discovery Section */}
                <section id="problem" className="scroll-mt-32" data-id="element-1014">
                  <h2 className="text-3xl font-light mb-12 text-[#001E79]" data-id="element-1015">
                    Problem & Discovery
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1016">
                    I inherited designs from a third-party agency that only
                    addressed the "happy path," but the live application
                    revealed significant problems across conditional user flows.
                  </p>

                  {/* Problem Statement */}
                  <div className="mb-16 border-l-2 border-[#001E79] pl-8 py-4" data-id="element-1017">
                    <h3 className="text-xs font-bold text-[#001E79] uppercase tracking-wider mb-6" data-id="element-1018">
                      Problem Statement
                    </h3>
                    <p className="text-2xl font-light leading-relaxed text-slate-900 mb-0" data-id="element-1019">
                      Corporate card applicants and internal processors
                      struggled with a{' '}
                      <span className="font-medium text-[#001E79]" data-id="element-1020">
                        fragmented 54-day application process
                      </span>{' '}
                      requiring extensive manual coordination across multiple
                      stakeholders, leading to{' '}
                      <span className="font-medium text-[#001E79]" data-id="element-1021">
                        confusion about task ownership, duplicated effort, and
                        inability to track application progress.
                      </span>
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-1022">
                    {[{
                    title: 'Disorganized Workflows',
                    desc: 'Missing Figma designs and development-design misalignment created confusion and delays.'
                  }, {
                    title: 'Inconsistent Design',
                    desc: 'Outdated components and poor navigation patterns hindered user experience.'
                  }, {
                    title: 'Accessibility Gaps',
                    desc: 'Poor contrast and non-compliant interactions limited usability and inclusivity.'
                  }, {
                    title: 'User Pain Points',
                    desc: 'Unclear navigation, limited user control, and dated visual identity frustrated users.'
                  }].map((card, idx) => <div key={idx} className="bg-gray-50 p-8 rounded-xl" data-id="element-1023">
                        <h4 className="font-medium text-lg mb-2 text-[#001E79]" data-id="element-1024">
                          {card.title}
                        </h4>
                        <p className="text-lg text-slate-600" data-id="element-1025">{card.desc}</p>
                      </div>)}
                  </div>
                </section>

                {/* Process Section */}
                <section id="solution" className="scroll-mt-32" data-id="element-1026">
                  <h2 className="text-3xl font-light mb-12 text-[#001E79]" data-id="element-1027">
                    Solution & Methodology
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1028">
                    Leading the design effort, I established a sustainable
                    workflow to coordinate our cross-functional team. With
                    product managers, content specialists, and developers all
                    working in shared Figma files, we needed a{' '}
                    <span className="font-semibold text-slate-800" data-id="element-1029">
                      single source of truth
                    </span>{' '}
                    that wouldn't create bottlenecks. I developed a three-phase
                    improvement strategy that enabled everyone to work
                    efficiently while maintaining consistency across the entire
                    application.
                  </p>

                  {/* Methodology Framework Visualization */}
                  <div className="mb-24" data-id="element-1030">
                    <h3 className="text-xl font-medium mb-8 text-[#001E79]" data-id="element-1031">
                      Strategic Approach
                    </h3>
                    <VerticalTimeline data-id="element-1032" />
                  </div>

                  <div className="space-y-32" data-id="element-1033">
                    {/* Step 1 */}
                    <div data-id="element-1034">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1035">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1036">
                          01
                        </span>
                        <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1037">
                          Chain Workflow System
                        </h3>
                      </div>

                      <div className="grid lg:grid-cols-2 gap-8 items-start" data-id="element-1038">
                        <div data-id="element-1039">
                          <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-1040">
                            I designed a{' '}
                            <span className="font-semibold text-slate-800" data-id="element-1041">
                              fluid content management system
                            </span>{' '}
                            where each piece of content lived as a component
                            that could be updated independently and
                            automatically sync across all design files. This
                            approach eliminated bottlenecks and enabled parallel
                            workflows.
                          </p>
                          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-1042">
                            Our content strategist and I could work
                            simultaneously without conflicts. Content updates
                            propagated instantly from template to design system
                            to all files. As screens moved into active sprints,
                            I marked them "Ready for Dev," giving the team clear
                            visibility. Our product leader noted we moved{' '}
                            <span className="font-semibold text-slate-800" data-id="element-1043">
                              30% more efficiently
                            </span>{' '}
                            through screen updates.
                          </p>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-2xl" data-id="element-1044">
                          <SimpleImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Content_Template.png" alt="Workflow System" data-id="element-1045" />
                        </div>
                      </div>
                    </div>

                    {/* Step 2 */}
                    <div data-id="element-1046">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1047">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1048">
                          02
                        </span>
                        <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1049">
                          Accessibility as Definition of Done
                        </h3>
                      </div>

                      <div className="grid lg:grid-cols-2 gap-8 items-start" data-id="element-1050">
                        <div data-id="element-1051">
                          <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-1052">
                            I partnered with accessibility specialist Tracey
                            Jackson to ensure every screen met{' '}
                            <span className="font-semibold text-slate-800" data-id="element-1053">
                              WCAG 2.1 standards
                            </span>
                            . The accessibility annotations you see here became
                            part of our "Ready for Dev" phase, our team's{' '}
                            <span className="font-semibold text-slate-800" data-id="element-1054">
                              definition of done
                            </span>{' '}
                            before any design went to engineering.
                          </p>
                          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-1055">
                            Content and I would review these annotations
                            together, syncing with the organization's design
                            system while discussing improvements. This
                            collaboration directly{' '}
                            <span className="font-semibold text-slate-800" data-id="element-1056">
                              improved usability for users with visual
                              disabilities
                            </span>{' '}
                            and gave engineers clear, actionable specifications.
                          </p>
                        </div>

                        <div className="bg-gray-50 p-6 rounded-2xl" data-id="element-1057">
                          <SimpleImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Annotations_master_example.png" alt="Accessibility Annotations" data-id="element-1058" />
                        </div>
                      </div>
                    </div>

                    {/* Step 3 */}
                    <div data-id="element-1059">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1060">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1061">
                          03
                        </span>
                        <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1062">
                          Design System Alignment
                        </h3>
                      </div>
                      <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1063">
                        I collaborated with the design system team to establish
                        a consistent, modern aesthetic. I{' '}
                        <span className="font-semibold text-slate-800" data-id="element-1064">
                          updated 75% of outdated components
                        </span>{' '}
                        and created nearly 20 new local components, including a
                        vertical navigation bar that was later adopted by other
                        teams.
                      </p>
                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-1065">
                        In the screens presented here, the progress bar was
                        removed from the "Get Started" screen by design. After
                        analyzing user feedback, I discovered the 5-step count
                        created a perception of length that didn't match the
                        actual effort required. By consolidating the intro
                        screen into the "Get Started" flow, we{' '}
                        <span className="font-semibold text-slate-800" data-id="element-1066">
                          reduced the visible step count to 4
                        </span>
                        . Users noted the process felt significantly shorter,
                        even though we'd only reduced the perceived complexity,
                        not the actual screens.
                      </p>

                      <div className="grid md:grid-cols-2 gap-8" data-id="element-1067">
                        <div data-id="element-1068">
                          <p className="text-sm font-medium text-slate-500 mb-4 uppercase tracking-wide" data-id="element-1069">
                            Before
                          </p>
                          <LightboxImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Old_design.png" alt="Old Design" data-id="element-1070" />
                        </div>
                        <div data-id="element-1071">
                          <p className="text-sm font-medium text-slate-500 mb-4 uppercase tracking-wide" data-id="element-1072">
                            After
                          </p>
                          <LightboxImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/New_Design.png" alt="New Design" data-id="element-1073" />
                        </div>
                      </div>

                      <div className="mt-8 p-6 bg-gray-50 rounded-lg border-l-4 border-[#001E79]" data-id="element-1074">
                        <p className="text-sm text-slate-600 leading-relaxed" data-id="element-1075">
                          <span className="font-semibold text-slate-800" data-id="element-1076">
                            Note:
                          </span>{' '}
                          This example represents one of several screens I was
                          permitted to showcase. This comprehensive process—user
                          testing, peer review, accessibility validation, design
                          system refinement, and post-launch optimization—was
                          applied across dozens of screens throughout the
                          application.
                        </p>
                      </div>
                    </div>

                    {/* Step 4 */}
                    <div data-id="element-1077">
                      <div className="flex items-center gap-4 mb-6" data-id="element-1078">
                        <span className="text-4xl font-light text-slate-300" data-id="element-1079">
                          04
                        </span>
                        <h3 className="text-2xl font-medium text-[#001E79]" data-id="element-1080">
                          Reimagined User Journey
                        </h3>
                      </div>
                      <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1081">
                        With our new workflow running smoothly, I finally had
                        the bandwidth to tackle the user experience improvements
                        that had been on the backlog. The streamlined process
                        gave me room to{' '}
                        <span className="font-semibold text-slate-800" data-id="element-1082">
                          map out complex user flows
                        </span>{' '}
                        that would have been impossible to manage before.
                      </p>
                      <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-3xl" data-id="element-1083">
                        The most impactful change was introducing a
                        <span className="font-semibold text-slate-800" data-id="element-1084">
                          {' '}
                          collaborative workflow system
                        </span>{' '}
                        for the application itself. Though it looks simple, a
                        collaborators section in the lower left, this feature
                        required extensive user flow mapping. It gave
                        administrators clear visibility into who was responsible
                        for what, and let multiple stakeholders complete their
                        assigned portions independently. Account owners could
                        now{' '}
                        <span className="font-semibold text-slate-800" data-id="element-1085">
                          reassign roles on the fly
                        </span>
                        , and users could skip questions and return later,
                        addressing two of our biggest pain points.
                      </p>
                      <div className="bg-gray-50 p-8 rounded-2xl" data-id="element-1086">
                        <SimpleImage src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/bdovfglt8tusyvarlsnd9f/5eeb8de3-4a4e-4cf2-9740-92477376f38e/Collaborators.png" alt="Collaborators Section" data-id="element-1087" />
                      </div>
                    </div>
                  </div>
                </section>

                {/* Results Section */}
                <section id="impact" className="scroll-mt-32" data-id="element-1088">
                  <h2 className="text-3xl font-light mb-12 text-[#001E79]" data-id="element-1089">
                    Business Impact & Outcomes
                  </h2>

                  <p className="text-lg text-slate-600 leading-relaxed mb-12 max-w-3xl" data-id="element-1090">
                    The new application sections launched to positive feedback
                    from both users and stakeholders. What started as a chaotic
                    design process became a model of collaboration and
                    efficiency that other teams at the bank started to
                    reference.
                  </p>

                  {/* Data Visualizations */}
                  <div className="space-y-16 mb-24" data-id="element-1091">
                    {/* Time Reduction & Satisfaction Grid */}
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-1092">
                      {/* Time Reduction */}
                      <div className="bg-white border border-gray-200 rounded-xl p-8 sm:p-10 shadow-sm hover:border-gray-300 transition-all duration-300 flex flex-col" data-id="element-1093">
                        <div className="flex items-center gap-4 mb-8" data-id="element-1094">
                          <div className="w-12 h-12 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center text-[#001E79] flex-shrink-0" data-id="element-1095">
                            <Clock className="w-6 h-6" data-id="element-1096" />
                          </div>
                          <h3 className="text-xl font-medium text-[#001E79]" data-id="element-1097">
                            Processing Time Reduction
                          </h3>
                        </div>

                        <div className="flex-1 flex items-center justify-center gap-8 py-4" data-id="element-1098">
                          <div className="text-center" data-id="element-1099">
                            <div className="text-7xl font-light text-slate-400 mb-3" data-id="element-1100">
                              54
                            </div>
                            <div className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-2" data-id="element-1101">
                              Days Before
                            </div>
                            <div className="text-sm text-slate-500" data-id="element-1102">
                              Manual process
                            </div>
                          </div>

                          <div className="flex items-center justify-center" data-id="element-1103">
                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-[#001E79]" data-id="element-1104">
                              <path d="M8 16h16m0 0l-6-6m6 6l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1105" />
                            </svg>
                          </div>

                          <div className="text-center" data-id="element-1106">
                            <div className="text-7xl font-light text-[#001E79] mb-3" data-id="element-1107">
                              20
                            </div>
                            <div className="text-sm font-medium text-[#001E79] uppercase tracking-wide mb-2" data-id="element-1108">
                              Days After
                            </div>
                            <div className="text-sm text-slate-500" data-id="element-1109">
                              Digital platform
                            </div>
                          </div>
                        </div>

                        <div className="pt-6 border-t border-gray-100 text-center mt-auto" data-id="element-1110">
                          <div className="inline-flex items-center gap-2 bg-gray-50 text-slate-700 px-5 py-3 rounded-lg" data-id="element-1111">
                            <TrendUp className="w-5 h-5 text-[#001E79]" data-id="element-1112" />
                            <span className="text-base font-semibold" data-id="element-1113">
                              65% Faster Processing
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Satisfaction */}
                      <div className="bg-white border border-gray-200 rounded-xl p-8 sm:p-10 shadow-sm hover:border-gray-300 transition-all duration-300 flex flex-col" data-id="element-1114">
                        <div className="flex items-center gap-4 mb-8" data-id="element-1115">
                          <div className="w-12 h-12 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center text-[#001E79] flex-shrink-0" data-id="element-1116">
                            <Users className="w-6 h-6" data-id="element-1117" />
                          </div>
                          <h3 className="text-xl font-medium text-[#001E79]" data-id="element-1118">
                            Customer Satisfaction
                          </h3>
                        </div>

                        <div className="flex-1 flex flex-col items-center justify-center py-4" data-id="element-1119">
                          <div className="text-7xl font-light text-[#001E79] mb-3" data-id="element-1120">
                            98%
                          </div>
                          <div className="text-sm font-medium text-slate-600 uppercase tracking-wide mb-2" data-id="element-1121">
                            Rated "Easy" or "Very Easy"
                          </div>
                          <div className="text-sm text-slate-500" data-id="element-1122">
                            Exceeded 90% target
                          </div>
                        </div>

                        <div className="pt-6 border-t border-gray-100 text-center mt-auto" data-id="element-1123">
                          <div className="inline-flex items-center gap-2 bg-gray-50 text-slate-700 px-5 py-3 rounded-lg" data-id="element-1124">
                            <CheckCircle className="w-5 h-5 text-[#001E79]" data-id="element-1125" />
                            <span className="text-base font-semibold" data-id="element-1126">
                              Target Achieved
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Automation Impact Stats */}
                    <div data-id="element-1127">
                      <h3 className="text-2xl font-medium mb-8 text-[#001E79]" data-id="element-1128">
                        Automation & Productivity Impact
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1129">
                        {[{
                        label: 'Automated Processes',
                        value: '17+',
                        icon: Lightning,
                        desc: 'New workflows implemented'
                      }, {
                        label: 'Annual Hours Saved',
                        value: '20k',
                        icon: Clock,
                        desc: 'Productivity unlocked'
                      }, {
                        label: 'Pipeline Migrated',
                        value: '96%',
                        icon: TrendUp,
                        desc: 'To digital onboarding'
                      }, {
                        label: 'Time Reduction',
                        value: '65%',
                        icon: ChartBar,
                        desc: 'Faster processing'
                      }].map((stat, idx) => <div key={idx} className="bg-white border border-gray-200 rounded-xl p-8 sm:p-10 shadow-sm hover:border-gray-300 transition-all duration-300 flex flex-col items-center text-center" data-id="element-1130">
                            <div className="w-14 h-14 bg-gray-50 border border-gray-200 rounded-xl flex items-center justify-center mb-6 text-[#001E79]" data-id="element-1131">
                              <stat.icon className="w-7 h-7" data-id="element-1132" />
                            </div>
                            <div className="text-5xl sm:text-6xl font-light text-[#001E79] mb-4" data-id="element-1133">
                              {stat.value}
                            </div>
                            <div className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-3" data-id="element-1134">
                              {stat.label}
                            </div>
                            <div className="text-base text-slate-500" data-id="element-1135">
                              {stat.desc}
                            </div>
                          </div>)}
                      </div>
                    </div>

                    {/* User Feedback Notes */}
                    <div data-id="element-1136">
                      <h3 className="text-2xl font-medium mb-4 text-[#001E79]" data-id="element-1137">
                        Key Takeaways from Stakeholder Review
                      </h3>
                      <p className="text-base text-slate-600 mb-8" data-id="element-1138">
                        User feedback collected during final review sessions
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1139">
                        {[{
                        note: 'Navigation was clear and accessible, making it easy to find what I needed',
                        label: 'Clear Navigation'
                      }, {
                        note: 'The collaborator view and edit was now easy to access for my whole team',
                        label: 'Collaborator Access'
                      }, {
                        note: 'Content is easy to read with no jargon, finally feels professional',
                        label: 'Clear Content'
                      }, {
                        note: "'Skip for Now' was greatly appreciated by those working through the application at their own pace",
                        label: 'Flexible Completion'
                      }].map((item, idx) => <div key={idx} className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg" data-id="element-1140">
                            <p className="text-sm text-slate-700 mb-3 italic" data-id="element-1141">
                              "{item.note}"
                            </p>
                            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide" data-id="element-1142">
                              {item.label}
                            </p>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </section>

                {/* Reflection Section */}
                <section id="reflection" className="scroll-mt-32" data-id="element-1143">
                  <h2 className="text-3xl font-light mb-12 text-[#001E79]" data-id="element-1144">
                    Reflection
                  </h2>

                  <div className="bg-gray-50 rounded-2xl p-8 md:p-12" data-id="element-1145">
                    <p className="text-lg text-slate-600 leading-relaxed mb-6 max-w-3xl" data-id="element-1146">
                      Before my departure, our product owner highlighted these
                      achievements in a stakeholder presentation, and on my last
                      day, I had a chance to chat with my team members who
                      expressed their appreciation for my efforts. I truly
                      enjoyed working with everyone and made many friends along
                      the way.
                    </p>

                    <h3 className="text-xl font-medium mb-6 text-[#001E79]" data-id="element-1147">
                      What I Learned
                    </h3>
                    <ul className="space-y-3 max-w-3xl" data-id="element-1148">
                      <li className="flex items-start gap-3" data-id="element-1149">
                        <CheckCircle className="w-5 h-5 text-[#001E79] mt-1 flex-shrink-0" data-id="element-1150" />
                        <span className="text-slate-700" data-id="element-1151">
                          Establishing clear workflows early prevents
                          bottlenecks and enables parallel work across
                          disciplines
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-1152">
                        <CheckCircle className="w-5 h-5 text-[#001E79] mt-1 flex-shrink-0" data-id="element-1153" />
                        <span className="text-slate-700" data-id="element-1154">
                          Involving developers early in the design process
                          provides technical context that shapes more focused
                          end goals and realistic solutions
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-1155">
                        <CheckCircle className="w-5 h-5 text-[#001E79] mt-1 flex-shrink-0" data-id="element-1156" />
                        <span className="text-slate-700" data-id="element-1157">
                          Engaging accessibility specialists from the start
                          ensures all levels of design strategy are accounted
                          for, creating a wealth of context the team can
                          leverage when addressing business directives
                        </span>
                      </li>
                      <li className="flex items-start gap-3" data-id="element-1158">
                        <CheckCircle className="w-5 h-5 text-[#001E79] mt-1 flex-shrink-0" data-id="element-1159" />
                        <span className="text-slate-700" data-id="element-1160">
                          User research can challenge standard UX practices.
                          While breaking up steps is conventional wisdom, our
                          data showed users preferred fewer perceived steps,
                          proving the importance of validating assumptions with
                          actual user feedback
                        </span>
                      </li>
                    </ul>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {!isOverlay && <CaseStudyNavigation homePath={homePath} previousCase={{
          category: 'Featured Work',
          title: 'CWRU',
          slug: 'case-western'
        }} nextCase={{
          category: 'Case Studies',
          title: 'Cyber Security',
          slug: 'cyber-security'
        }} data-id="element-1161" />}

          {!isOverlay && <Footer data-id="element-1162" />}
        </div>

        {!isOverlay && <ChatDrawer onCaseStudyClick={handleCaseStudyClick} data-id="element-1163" />}
      </div>

      {!isOverlay && !isChatDrawerOpen && <FloatingButtons data-id="element-1164" />}
    </>;
}